import { useEffect, useState } from 'react'
import { useAuth, Card } from '@trafiflow/shared-ui'
import { API_CONFIG } from '@/shared/config'

export default function Profile() {
    const { user, refreshUser } = useAuth()
    const [nextStoreTenantId, setNextStoreTenantId] = useState<string | null>(null)
    const [organizationSlug, setOrganizationSlug] = useState<string | null>(null)
    const [loadingAgentplat, setLoadingAgentplat] = useState(true)

    useEffect(() => {
        refreshUser()
    }, [refreshUser])

    useEffect(() => {
        console.log('[Profile] useEffect triggered, user.tenantId:', user?.tenantId)
        const fetchAgentPlatTenantId = async () => {
            try {
                console.log('[Profile] Starting fetchAgentPlatTenantId')
                setLoadingAgentplat(true)
                const apiUrl = API_CONFIG.baseUrl
                const { getAuthToken } = await import('@trafiflow/shared-ui')
                const token = await getAuthToken()

                console.log('[Profile] Fetching from:', `${apiUrl}/profile/agentplat-tenant-id`)
                const response = await fetch(`${apiUrl}/profile/agentplat-tenant-id`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                })

                console.log('[Profile] Response status:', response.status)
                if (response.ok) {
                    const result = await response.json()
                    console.log('[Profile] API Response:', result)
                    if (result.success && result.data) {
                        setNextStoreTenantId(result.data.nextStoreTenantId || null)
                        setOrganizationSlug(result.data.organizationSlug || null)
                        console.log('[Profile] Set values:', {
                            nextStoreTenantId: result.data.nextStoreTenantId,
                            organizationSlug: result.data.organizationSlug
                        })
                    }
                } else {
                    const errorText = await response.text()
                    console.error('[Profile] API Error:', response.status, errorText)
                }
            } catch (err) {
                console.error('[Profile] Error fetching AgentPlat tenant ID:', err)
            } finally {
                setLoadingAgentplat(false)
            }
        }

        if (user?.tenantId) {
            fetchAgentPlatTenantId()
        } else {
            console.log('[Profile] No user.tenantId, skipping fetch')
        }
    }, [user?.tenantId])

    if (!user) {
        return null
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-text">Profile</h1>
            </div>

            <Card className="p-6">
                <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center text-white text-xl font-bold">
                            {user.email.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                            <h2 className="text-lg font-medium text-text">{user.email}</h2>
                            <p className="text-text-subdued">User Account</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-border-subdued">
                        <div>
                            <label className="block text-sm font-medium text-text-subdued mb-1">
                                Email
                            </label>
                            <div className="text-text font-medium">{user.email}</div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-subdued mb-1">
                                User ID
                            </label>
                            <div className="text-text font-medium font-mono text-xs">{user.userId}</div>
                        </div>

                        <div className="col-span-1 md:col-span-2">
                            <label className="block text-sm font-medium text-text-subdued mb-1">
                                Tenant (Organization ID)
                            </label>
                            <div className="space-y-2">
                                <div>
                                    <div className="text-xs text-text-subdued mb-1">Tenant ID:</div>
                                    <div className="text-text font-medium font-mono bg-surface-subdued p-3 rounded border border-border-subdued">
                                        {loadingAgentplat ? (
                                            <span className="text-text-subdued italic">Loading...</span>
                                        ) : (
                                            nextStoreTenantId || user.tenantId || 'No Tenant Assigned'
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-text-subdued mb-1">Slug:</div>
                                    <div className="text-text font-medium font-mono bg-surface-subdued p-3 rounded border border-border-subdued">
                                        {loadingAgentplat ? (
                                            <span className="text-text-subdued italic">Loading...</span>
                                        ) : organizationSlug ? (
                                            organizationSlug
                                        ) : (nextStoreTenantId && !/^\d+-\d+$/.test(nextStoreTenantId)) ? (
                                            // Si nextStoreTenantId no es numérico, es el slug
                                            nextStoreTenantId
                                        ) : (user.tenantId && !/^\d+-\d+$/.test(user.tenantId)) ? (
                                            // Si user.tenantId no es numérico, es el slug
                                            user.tenantId
                                        ) : (
                                            <span className="text-text-subdued italic">No slug available</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <p className="text-xs text-text-subdued mt-1">
                                This ID is used to isolate your organization's data.
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-subdued mb-1">
                                Roles
                            </label>
                            <div className="flex gap-2">
                                {user.roles?.map((role) => (
                                    <span
                                        key={role}
                                        className="px-2 py-1 bg-primary-lighter text-primary-dark rounded text-xs font-medium"
                                    >
                                        {role}
                                    </span>
                                )) || <span className="text-text-subdued italic">No roles assigned</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    )
}
